import { Instance, onSnapshot, SnapshotIn, types } from 'mobx-state-tree';
import { createContext, useContext } from 'react';

import { Storage } from '@ionic/storage';
import { isInDebugMode } from '../../util';
import { Area } from 'services/Database/CloudStorageService';
import _ from 'lodash';

export const DebugStoreModel = types
  .model({
    auth: types.enumeration(['Firebase', 'Dummy']),
    storage: types.enumeration(['Firebase', 'Dummy']),
    bluetooth: types.enumeration(['Dummy', 'BLE']),
    location: types.enumeration(['Dummy', 'Smartphone']),
    api: types.enumeration(['Dummy', 'Server']),
    useFirebaseEmulator: types.boolean,
    trickStore: types.enumeration<Area>('Area', _.values(Area)),
    searchTime: types.number,
    useMockData: types.boolean,
  })
  .actions((self) => ({
    setAuth(auth: 'Firebase' | 'Dummy') {
      self.auth = auth;
    },
    setApi(api: 'Server' | 'Dummy') {
      self.api = api;
    },
    setBluetooth(blue: 'Dummy' | 'BLE') {
      self.bluetooth = blue;
    },
    setLocation(loc: 'Smartphone' | 'Dummy') {
      self.location = loc;
    },
    setStorage(storage: 'Firebase' | 'Dummy') {
      self.storage = storage;
    },
    setUseFirebaseEmulator(value: boolean) {
      self.useFirebaseEmulator = value;
    },
    setRunStore(value: Area) {
      self.trickStore = value;
    },
    setSearchTime(value: number) {
      self.searchTime = value * 1000;
    },
    setUseMockData(value: boolean) {
      self.useMockData = value;
    },
  }));

const DEBUG_STATE_STORAGE_KEY = 'debugStore';
const IS_DBG = isInDebugMode();
export const setupDebugStore = async () => {
  let store: DebugStore;
  let data: any;

  const storage = new Storage();
  await storage.create();

  const emptyState: DebugStoreSnapshotIn = {
    auth: 'Firebase',
    storage: 'Firebase',
    bluetooth: 'BLE',
    location: 'Smartphone',
    api: 'Server',
    useFirebaseEmulator: false,
    trickStore: IS_DBG ? Area.test : Area.live,
    searchTime: 10000,
    // Enable mock data in both debug and production modes
    useMockData: true, // Now enabled in all environments
  };

  try {
    if (IS_DBG) {
      // In debug mode, allow saved settings and URL parameters
      data = { ...emptyState, ...(await storage.get(DEBUG_STATE_STORAGE_KEY)) };
    } else {
      // In production mode, use default settings (with mock data enabled)
      data = emptyState;
    }
    store = DebugStoreModel.create(data);
  } catch (e: unknown) {
    store = DebugStoreModel.create(emptyState);
  }

  // Only save debug state changes in debug mode
  if (IS_DBG) {
    onSnapshot(store, (snapshot) =>
      storage.set(DEBUG_STATE_STORAGE_KEY, snapshot)
    );

    // Check URL parameters for mock data override only in debug mode
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('mockData')) {
      const mockDataValue = urlParams.get('mockData') === 'true';
      store.setUseMockData(mockDataValue);
    }
  }

  return store;
};

export interface DebugStore extends Instance<typeof DebugStoreModel> {}
export interface DebugStoreSnapshotIn
  extends SnapshotIn<typeof DebugStoreModel> {}

const DebugStoreContext = createContext<DebugStore>({} as DebugStore);

export const DebugStoreProvider = DebugStoreContext.Provider;

export const useDebugStore = () => useContext(DebugStoreContext);
